import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

export const supportedLanguages: Language[] = [
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

// Update HTML lang attribute
const updateHtmlLang = (language: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
};

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    return supportedLanguages.find(lang => lang.code === i18n.language) || supportedLanguages[0];
  });

  // Update current language when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLanguage = supportedLanguages.find(lang => lang.code === lng);
      if (newLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };

    // Listen to i18next language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Listen to custom language change events
    const handleCustomLanguageChange = (event: CustomEvent) => {
      const { language } = event.detail;
      const newLanguage = supportedLanguages.find(lang => lang.code === language);
      if (newLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };

    window.addEventListener('languageChanged', handleCustomLanguageChange as EventListener);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleCustomLanguageChange as EventListener);
    };
  }, [i18n]);

  const changeLanguage = useCallback(async (languageCode: string) => {
    try {
      const targetLanguage = supportedLanguages.find(lang => lang.code === languageCode);
      if (!targetLanguage) {
        throw new Error(`Unsupported language: ${languageCode}`);
      }

      // Change language using i18next
      await i18n.changeLanguage(languageCode);
      
      // Ensure localStorage is updated
      localStorage.setItem('i18nextLng', languageCode);
      localStorage.setItem('language', languageCode);
      
      // Update state
      setCurrentLanguage(targetLanguage);
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { language: languageCode } 
      }));

      // Update HTML lang attribute
      updateHtmlLang(languageCode);

      return true;
    } catch (error) {
      console.error('Failed to change language:', error);
      return false;
    }
  }, [i18n]);

  // Update HTML lang on mount
  useEffect(() => {
    updateHtmlLang(currentLanguage.code);
  }, [currentLanguage]);

  const getLanguageFromStorage = useCallback((): string => {
    const stored = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
    if (stored && supportedLanguages.some(lang => lang.code === stored)) {
      return stored;
    }
    return 'no'; // Default to Norwegian
  }, []);

  const isLanguageSupported = useCallback((languageCode: string): boolean => {
    return supportedLanguages.some(lang => lang.code === languageCode);
  }, []);

  return {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    getLanguageFromStorage,
    isLanguageSupported,
    isReady: i18n.isInitialized,
  };
};

export default useLanguage; 